import { Timestamp } from "@bufbuild/protobuf";

import React, { useContext, useEffect, useState } from "react";

import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import mainStyles from "styles";

import {
  ModuleSubscription,
  ModuleSubscriptionState,
  SubscriptionState,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import ActivateModuleSubscription from "./subscription-actions/activate-module-subscription";
import AllocateModuleComponents from "./subscription-actions/allocate-module-components";
import ChangeModuleTrialDate from "./subscription-actions/change-module-trial-date";
import StartModuleTrial from "./subscription-actions/start-module-trial";
import SubscriptionAction from "./subscription-actions/subscription-action";
import { getModuleSubscription } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import { IdValue } from "components/org-display/dashboard/cards/card-shell";
import { SubDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import {
  convertTimestampToDate,
  formatDateDisplayOmittingMidnight,
} from "helpers/time";

// Only Construction Sign On exists today (FG-8769) — extend this list as
// more modules ship trials (FG-8756).
const MODULES: Array<{ moduleName: string; label: string }> = [
  { moduleName: "sign_on_site", label: "Construction Sign On" },
];

const STATE_LABEL: Record<ModuleSubscriptionState, string> = {
  [ModuleSubscriptionState.UNSPECIFIED]: "Unknown",
  [ModuleSubscriptionState.NOT_OWNED]: "Not owned",
  [ModuleSubscriptionState.TRIAL]: "Trial",
  [ModuleSubscriptionState.TRIAL_ENDED]: "Trial ended",
  [ModuleSubscriptionState.ACTIVE]: "Active",
  [ModuleSubscriptionState.ACTIVATION_FAILED]: "Activation failed",
};

interface ModuleSubscriptionsTabProps {
  roleId: string;
  regionCode: string;
  refreshOrgData: () => void;
}

const ModuleSubscriptionsTab = ({
  roleId,
  regionCode,
  refreshOrgData,
}: ModuleSubscriptionsTabProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { subData } = useContext(SubDataContext);
  const { copyToClipboard } = useContext(SnackbarContext);

  const platformTrialEnd =
    subData?.subscription?.state === SubscriptionState.TRIALING &&
    subData.subscription.trialEndsAt
      ? convertTimestampToDate(subData.subscription.trialEndsAt)
      : undefined;

  // Same status-pill palette as SummaryStatusPills — TRIAL maps to blueMoon
  // to match the existing TRIALING subscription-state pill.
  const stateStyle: Record<ModuleSubscriptionState, object> = {
    [ModuleSubscriptionState.UNSPECIFIED]: classes.neutral,
    [ModuleSubscriptionState.NOT_OWNED]: classes.neutral,
    [ModuleSubscriptionState.TRIAL]: classes.blueMoon,
    [ModuleSubscriptionState.TRIAL_ENDED]: classes.redWine,
    [ModuleSubscriptionState.ACTIVE]: classes.greenDay,
    [ModuleSubscriptionState.ACTIVATION_FAILED]: classes.redWine,
  };

  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<
    Record<string, ModuleSubscription | undefined>
  >({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const canGetModuleSubscription = hasPermissions([
    ActionType.GET_MODULE_SUBSCRIPTION,
  ]);
  const canStartModuleTrial = hasPermissions([ActionType.START_MODULE_TRIAL]);
  const canActivateModuleSubscription = hasPermissions([
    ActionType.ACTIVATE_MODULE_SUBSCRIPTION,
  ]);
  const canAllocateComponents = hasPermissions([
    ActionType.ALLOCATE_COMPONENTS,
  ]);

  // Re-fetches a single module's subscription after a mutation (start trial /
  // allocate quantity) so the displayed state updates without a page reload.
  // Unlike the bulk fetch below, this isn't subject to the org-switch race it
  // guards against — it's fired by a user action on the already-loaded tab.
  const refetchModule = async (moduleName: string) => {
    try {
      const res = await getModuleSubscription(
        { orgId: roleId, moduleName },
        callOptionsHeaders(regionCode, roleId),
      );
      setSubscriptions((prev) => ({ ...prev, [moduleName]: res.subscription }));
      setErrors((prev) => ({ ...prev, [moduleName]: undefined }));
    } catch (error) {
      const message = (error as GrpcError).message || "Failed to load";
      setErrors((prev) => ({ ...prev, [moduleName]: message }));
    }
  };

  useEffect(() => {
    if (permissions.isPermissionsLoading || !canGetModuleSubscription) {
      return;
    }
    // The org page doesn't remount this tab on org/region switch, so a
    // slower earlier request can otherwise resolve after a newer one and
    // overwrite it with stale data — ignore results once superseded.
    let isStale = false;
    setIsLoading(true);
    Promise.all(
      MODULES.map(({ moduleName }) =>
        getModuleSubscription(
          { orgId: roleId, moduleName },
          callOptionsHeaders(regionCode, roleId),
        )
          .then((res) => [moduleName, res.subscription, undefined] as const)
          .catch((error) => {
            const message = (error as GrpcError).message || "Failed to load";
            return [moduleName, undefined, message] as const;
          }),
      ),
    )
      .then((results) => {
        if (isStale) return;
        setSubscriptions(
          Object.fromEntries(
            results.map(([moduleName, subscription]) => [
              moduleName,
              subscription,
            ]),
          ),
        );
        setErrors(
          Object.fromEntries(
            results.map(([moduleName, , message]) => [moduleName, message]),
          ),
        );
      })
      .finally(() => {
        if (!isStale) setIsLoading(false);
      });
    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    roleId,
    regionCode,
    permissions.isPermissionsLoading,
    canGetModuleSubscription,
  ]);

  if (permissions.isPermissionsLoading) {
    return (
      <Box sx={{ pt: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!canGetModuleSubscription) {
    return (
      <Box sx={{ pt: 2 }}>
        <Typography sx={{ pt: 2 }}>
          You do not have permission to view module subscriptions.
        </Typography>
      </Box>
    );
  }

  const handleModuleTrialDateUpdated =
    (moduleName: string) => (newTrialEndsAt: Timestamp) => {
      setSubscriptions((prev) => {
        const existing = prev[moduleName];
        if (!existing) {
          return prev;
        }
        const updated = existing.clone();
        updated.trialEndsAt = newTrialEndsAt;
        return { ...prev, [moduleName]: updated };
      });

      // A trialing platform subscription can itself be extended to match a module trial
      // extended past it (see extendPlatformTrialIfLater in api-subscriptions) - refresh the
      // shared subscription context so the Subscription tab doesn't keep showing the stale
      // platform trial end date.
      if (platformTrialEnd) {
        refreshOrgData();
      }
    };

  return (
    <Box sx={{ pt: 2 }}>
      {isLoading && (
        <Box sx={{ pt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!isLoading &&
        MODULES.map(({ moduleName, label }) => {
          const subscription = subscriptions[moduleName];
          const error = errors[moduleName];
          return (
            <Box key={moduleName} sx={{ pt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {label}
              </Typography>
              <Box
                style={{
                  borderColor: theme.palette.divider,
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderRadius: "5px",
                  margin: "20px",
                  paddingBottom: "16px",
                }}
              >
                {error ? (
                  <Alert severity="error" data-testid={`${moduleName}-error`}>
                    Unable to load {label}: {error}
                  </Alert>
                ) : (
                  <Table size="medium">
                    <TableBody>
                      {subscription?.subscriptionId && (
                        <TableRow>
                          <TableCell sx={classes.tableCellNew}>
                            <SubscriptionAction
                              title="ID:"
                              middleElem={
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <IdValue
                                    value={toUUID(subscription.subscriptionId)}
                                    label="subscription UUID"
                                    onCopy={copyToClipboard}
                                    alternate={{
                                      value: subscription.subscriptionId,
                                      label: "s12",
                                    }}
                                    testIdPrefix={`${moduleName}-subscription-id`}
                                    action={
                                      <Tooltip title="Open the subscription document">
                                        <IconButton
                                          component={RouterLink}
                                          to={{
                                            pathname: `/search/doc/${subscription.subscriptionId}`,
                                          }}
                                          target="_blank"
                                          size="small"
                                          color="primary"
                                          aria-label="Open the subscription document"
                                          sx={{ p: 0.25, flex: "none" }}
                                          data-testid={`${moduleName}-subscription-doc-link`}
                                        >
                                          <LaunchIcon fontSize="inherit" />
                                        </IconButton>
                                      </Tooltip>
                                    }
                                  />
                                </Box>
                              }
                            >
                              <></>
                            </SubscriptionAction>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={classes.tableCellNew}>
                          <SubscriptionAction
                            title="Status:"
                            middleElem={
                              <Chip
                                label={
                                  subscription
                                    ? STATE_LABEL[subscription.state]
                                    : "Unavailable"
                                }
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  ...(subscription
                                    ? stateStyle[subscription.state]
                                    : classes.neutral),
                                }}
                              />
                            }
                          >
                            {canStartModuleTrial &&
                            subscription?.state ===
                              ModuleSubscriptionState.NOT_OWNED ? (
                              <StartModuleTrial
                                orgId={roleId}
                                regionCode={regionCode}
                                moduleName={moduleName}
                                moduleLabel={label}
                                onSuccess={() => refetchModule(moduleName)}
                              />
                            ) : (
                              <></>
                            )}
                            {canActivateModuleSubscription &&
                            (subscription?.state ===
                              ModuleSubscriptionState.TRIAL ||
                              subscription?.state ===
                                ModuleSubscriptionState.TRIAL_ENDED) ? (
                              <ActivateModuleSubscription
                                orgId={roleId}
                                regionCode={regionCode}
                                moduleName={moduleName}
                                moduleLabel={label}
                                onSuccess={() => refetchModule(moduleName)}
                              />
                            ) : (
                              <></>
                            )}
                          </SubscriptionAction>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={classes.tableCellNew}>
                          <SubscriptionAction
                            title="Quantity:"
                            middleElem={
                              <Typography
                                data-testid={`${moduleName}-quantity`}
                                sx={classes.tableTypography}
                              >
                                {subscription?.quantity ?? "—"}
                              </Typography>
                            }
                          >
                            {canAllocateComponents &&
                            subscription?.subscriptionId &&
                            subscription.state !==
                              ModuleSubscriptionState.TRIAL_ENDED ? (
                              <AllocateModuleComponents
                                orgId={roleId}
                                regionCode={regionCode}
                                moduleName={moduleName}
                                subscriptionId={subscription.subscriptionId}
                                currentQuantity={subscription.quantity}
                                onSuccess={() => refetchModule(moduleName)}
                              />
                            ) : (
                              <></>
                            )}
                          </SubscriptionAction>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={classes.tableCellNew}>
                          <SubscriptionAction
                            title="Trial ends:"
                            middleElem={
                              <Typography
                                data-testid={`${moduleName}-trial-ends-at`}
                                sx={classes.tableTypography}
                              >
                                {subscription?.trialEndsAt
                                  ? formatDateDisplayOmittingMidnight(
                                      convertTimestampToDate(
                                        subscription.trialEndsAt,
                                      ),
                                    )
                                  : "—"}
                              </Typography>
                            }
                          >
                            {subscription?.state ===
                            ModuleSubscriptionState.TRIAL ? (
                              <ChangeModuleTrialDate
                                moduleLabel={label}
                                moduleSubscription={subscription}
                                roleId={roleId}
                                regionCode={regionCode}
                                onUpdated={handleModuleTrialDateUpdated(
                                  moduleName,
                                )}
                              />
                            ) : (
                              <></>
                            )}
                          </SubscriptionAction>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

export default ModuleSubscriptionsTab;
